import { z } from 'zod'
import { ENVELOPE_LIMITS } from '../constants/envelope.js'
import { envelopeStatusSchema } from '../enums/envelope-status.enum.js'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import {
  fieldTypeSchema,
  REQUIRED_BY_DEFAULT_FIELD_TYPES,
  type FieldType,
} from '../enums/field-type.enum.js'
import { fieldValueDtoSchema } from '../dto/envelope.dto.js'
import { signerDeliverySchema } from '../enums/signer-delivery.enum.js'
import { emailSchema } from '../validators/email.validator.js'
import { uuidSchema } from '../validators/params.js'
import { signerDeadlineIssues } from '../validators/signer-deadline.validator.js'

const clientKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, 'Keys may contain letters, numbers, _ and -')

const fieldOptionInputSchema = z
  .object({
    key: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(200),
  })
  .strict()

const metadataSchema = z
  .record(
    z.string().trim().min(1).max(ENVELOPE_LIMITS.maxMetadataChars),
    z.string().trim().max(ENVELOPE_LIMITS.maxMetadataChars),
  )
  .refine(
    (value) => Object.keys(value).length <= ENVELOPE_LIMITS.maxMetadataKeys,
    `At most ${String(ENVELOPE_LIMITS.maxMetadataKeys)} metadata keys`,
  )

const remindPolicySchema = z
  .object({
    everyDays: z.number().int().min(1).max(365),
    max: z
      .number()
      .int()
      .min(1)
      .max(ENVELOPE_LIMITS.maxRemindsPerSigner)
      .default(ENVELOPE_LIMITS.defaultRemindMax)
      .describe(
        'Reminders per signer over the envelope’s life, from every source together (scheduled, API, dashboard, staff, AI apps). Default 5, at most 10.',
      ),
  })
  .strict()

const notifyCopyInputSchema = z
  .object({
    subject: z.string().trim().min(1).max(200).optional(),
    message: z.string().trim().min(1).max(5000).optional(),
  })
  .strict()

const envelopeNotifyInputSchema = z
  .object({
    remind: notifyCopyInputSchema
      .optional()
      .describe(
        'Copy for every reminder email on this envelope. Defaults to emailSubject/emailMessage.',
      ),
  })
  .strict()

const signerNotifyInputSchema = z
  .object({
    invite: notifyCopyInputSchema.optional(),
    remind: notifyCopyInputSchema.optional(),
  })
  .strict()

const documentInputSchema = z
  .object({
    key: clientKeySchema,
    filename: z.string().trim().min(1).max(255),
    title: z.string().trim().min(1).max(200),
    order: z.number().int().min(1).max(ENVELOPE_LIMITS.maxDocuments),
    html: z
      .string()
      .min(1)
      .max(ENVELOPE_LIMITS.maxHtmlBytes)
      .optional()
      .describe(
        'HTML for this document. XOR with a PDF file part named after the document key. Every field on this document needs one empty data-ps-field="{name}" element. Omit page/x/y/width/height on those fields.',
      ),
  })
  .strict()

const signerInputSchema = z
  .object({
    key: clientKeySchema,
    name: z.string().trim().min(1).max(200),
    email: emailSchema,
    role: z.string().trim().min(1).max(80),
    order: z.number().int().min(1).max(ENVELOPE_LIMITS.maxSigners),
    expiresAt: z
      .string()
      .datetime()
      .optional()
      .describe(
        'This signer\'s own deadline, earlier than the envelope\'s expiresAt, so a later signer (a countersigner, say) keeps time of their own. If the signer hasn\'t finished by then, the envelope expires. It can\'t be after the envelope\'s expiresAt, or before the deadline of a signer earlier in the order. Omit it to give the signer the envelope\'s.',
      ),
    redirectUrl: z
      .string()
      .url()
      .max(2000)
      .refine((value) => {
        const url = new URL(value)
        return url.protocol === 'https:' || url.protocol === 'http:'
      }, 'redirectUrl must be http(s)')
      .refine((value) => {
        const url = new URL(value)
        return url.protocol === 'https:' || url.hostname === 'localhost' || url.hostname === '127.0.0.1'
      }, 'redirectUrl must be HTTPS')
      .nullish(),
    delivery: signerDeliverySchema
      .optional()
      .describe(
        'Who gets this signer their link. `email` (the default): PixelSign emails the invite and reminders. `caller`: you hand them the signingUrl or embedUrl yourself, and PixelSign emails them only the completed copy. A later signer\'s turn fires signer.sent; get their link from POST /v1/envelopes/{id}/signers/{signerId}/links.',
      ),
    notify: signerNotifyInputSchema
      .optional()
      .describe(
        'Per-signer email overrides. Beats the envelope copy; subject and message each fall back on their own. Not allowed with delivery `caller`.',
      ),
    questions: z
      .object({
        answerer: z.union([
          z.object({ signerKey: clientKeySchema }).strict(),
          z
            .object({
              name: z.string().trim().min(1).max(200),
              email: emailSchema,
              relationship: z.string().trim().min(1).max(80),
            })
            .strict(),
        ]),
      })
      .strict()
      .optional(),
  })
  .strict()

const fieldInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .regex(/^[A-Za-z][A-Za-z0-9_.-]*$/, 'Field name must be a dotted identifier'),
    type: fieldTypeSchema.describe(
      'signature, initials, date_signed, date, text, checkbox, radio, or dropdown. HTML uses the same types; placement is the matching data-ps-field marker.',
    ),
    documentKey: clientKeySchema,
    signerKey: clientKeySchema,
    page: z
      .number()
      .int()
      .min(1)
      .optional()
      .describe('1-based PDF page. Required on PDF documents; omit for HTML.'),
    x: z
      .number()
      .finite()
      .min(0)
      .optional()
      .describe('PDF points from the top-left. Required on PDF documents; omit for HTML.'),
    y: z
      .number()
      .finite()
      .min(0)
      .optional()
      .describe('PDF points from the top-left. Required on PDF documents; omit for HTML.'),
    width: z
      .number()
      .finite()
      .gt(0)
      .optional()
      .describe('PDF points. Required on PDF documents; omit for HTML (size the marker with CSS).'),
    height: z
      .number()
      .finite()
      .gt(0)
      .optional()
      .describe('PDF points. Required on PDF documents; omit for HTML (size the marker with CSS).'),
    required: z.boolean().optional(),
    readOnly: z.boolean().optional(),
    value: fieldValueDtoSchema.optional(),
    group: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .nullish()
      .describe('Required for radio. All radios in a group share this string; the selected value is the field name.'),
    options: z
      .array(fieldOptionInputSchema)
      .max(50)
      .optional()
      .describe('Required for dropdown (at least two). The signer picks an option.key.'),
    maxLength: z.number().int().min(1).max(4000).optional(),
  })
  .strict()

export type FieldInput = z.infer<typeof fieldInputSchema>

/**
 * A prefilled radio that counts as chosen. Blank is unselected. A non-string
 * is returned as '' so it fails the "value is the field name" check.
 */
function chosenRadioValue(value: FieldInput['value']): string | undefined {
  if (value == null) return undefined
  if (typeof value !== 'string') return ''
  if (value.trim().length === 0) return undefined
  return value
}

function defaultRequired(type: FieldType): boolean {
  return REQUIRED_BY_DEFAULT_FIELD_TYPES.some((item) => item === type)
}

function uniqueStrings(values: string[]): boolean {
  return new Set(values).size === values.length
}

function isContiguousOrder(orders: number[]): boolean {
  if (orders.length === 0) {
    return false
  }
  const sorted = [...orders].sort((a, b) => a - b)
  return sorted.every((value, index) => value === index + 1)
}

/**
 * The optional `Idempotency-Key` header on create: 1–256 printable ASCII
 * characters. Length and charset are schema checks (not one opaque refine) so
 * the OpenAPI document can describe them.
 */
const idempotencyKeyPattern = /^[\x20-\x7E]+$/

export const idempotencyKeyHeaderSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(idempotencyKeyPattern, 'Idempotency-Key must be 1–256 printable ASCII characters')
  .optional()

export const createEnvelopeInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    emailSubject: z.string().trim().min(1).max(200),
    emailMessage: z.string().trim().min(1).max(5000),
    expiresAt: z.string().datetime(),
    remind: remindPolicySchema,
    retentionDays: z
      .number()
      .int()
      .min(1)
      .max(ENVELOPE_LIMITS.maxRetentionDays)
      .default(ENVELOPE_LIMITS.defaultRetentionDays)
      .describe(
        `How many days after the envelope finishes (completed, declined, voided, or expired) PixelSign keeps its files. Defaults to ${String(ENVELOPE_LIMITS.defaultRetentionDays)}; the maximum is ${String(ENVELOPE_LIMITS.maxRetentionDays)} (five years). Download anything you need to keep for longer.`,
      ),
    notify: envelopeNotifyInputSchema
      .optional()
      .describe(
        'Envelope-wide email overrides. emailSubject/emailMessage are the invite copy; notify.remind changes what reminders say. Per-signer overrides beat these.',
      ),
    send: z.boolean().optional(),
    webhookUrl: z
      .string()
      .url()
      .max(2000)
      .refine((value) => new URL(value).protocol === 'https:', 'webhookUrl must be HTTPS')
      .nullish(),
    metadata: metadataSchema.optional(),
    documents: z
      .array(documentInputSchema)
      .min(1)
      .max(ENVELOPE_LIMITS.maxDocuments),
    signers: z.array(signerInputSchema).min(1).max(ENVELOPE_LIMITS.maxSigners),
    fields: z.array(fieldInputSchema).min(1).max(ENVELOPE_LIMITS.maxFields),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (!uniqueStrings(value.documents.map((document) => document.key))) {
      ctx.addIssue({
        code: 'custom',
        path: ['documents'],
        message: 'Document keys must be unique',
        params: { code: 'document.duplicate_key' },
      })
    }
    if (!uniqueStrings(value.signers.map((signer) => signer.key))) {
      ctx.addIssue({
        code: 'custom',
        path: ['signers'],
        message: 'Signer keys must be unique',
        params: { code: 'signer.duplicate_key' },
      })
    }
    if (!uniqueStrings(value.signers.map((signer) => signer.email))) {
      ctx.addIssue({
        code: 'custom',
        path: ['signers'],
        message: 'Signer emails must be unique on an envelope',
        params: { code: 'signer.duplicate_email' },
      })
    }
    for (const issue of signerDeadlineIssues(value.expiresAt, value.signers)) {
      ctx.addIssue({
        code: 'custom',
        path: ['signers', issue.index, 'expiresAt'],
        message: issue.message,
        params: { code: issue.code },
      })
    }
    if (!uniqueStrings(value.fields.map((field) => field.name))) {
      ctx.addIssue({
        code: 'custom',
        path: ['fields'],
        message: 'Field names must be unique on an envelope',
        params: { code: 'field.duplicate_name' },
      })
    }
    for (const [index, signer] of value.signers.entries()) {
      if (signer.delivery === 'caller' && signer.notify !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['signers', index, 'notify'],
          message: 'A caller-delivered signer gets no invite or reminder for notify copy to apply to',
          params: { code: 'signer.notify_unused' },
        })
      }
    }
    if (!isContiguousOrder(value.documents.map((document) => document.order))) {
      ctx.addIssue({
        code: 'custom',
        path: ['documents'],
        message: 'Document order must be a contiguous 1…N',
        params: { code: 'document.order_not_contiguous' },
      })
    }

    const documentKeys = new Set(value.documents.map((document) => document.key))
    const signerKeys = new Set(value.signers.map((signer) => signer.key))

    for (const [index, field] of value.fields.entries()) {
      if (!documentKeys.has(field.documentKey)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'documentKey'],
          message: `Unknown document key "${field.documentKey}"`,
          params: { code: 'field.unknown_document' },
        })
      }
      if (!signerKeys.has(field.signerKey)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'signerKey'],
          message: `Unknown signer key "${field.signerKey}"`,
          params: { code: 'field.unknown_signer' },
        })
      }
      const readOnly = field.readOnly ?? false
      if (field.type === 'date_signed' && field.readOnly === false) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'readOnly'],
          message: 'date_signed fields are always read-only',
          params: { code: 'field.date_signed_editable' },
        })
      }
      if (readOnly && field.type !== 'date_signed' && field.value === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'value'],
          message: 'Read-only fields must have a value',
          params: { code: 'field.read_only_without_value' },
        })
      }
      if (field.type === 'dropdown') {
        const options = field.options ?? []
        if (options.length < 2) {
          ctx.addIssue({
            code: 'custom',
            path: ['fields', index, 'options'],
            message: 'Dropdown fields need at least two options',
            params: { code: 'field.dropdown_options' },
          })
        }
      }
      if (field.type === 'radio' && (field.group == null || field.group === '')) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'group'],
          message: 'Radio fields require a group',
          params: { code: 'field.radio_group_missing' },
        })
      }
      const htmlDocument = value.documents.find(
        (document) => document.key === field.documentKey && document.html !== undefined,
      )
      const hasBox =
        field.page !== undefined &&
        field.x !== undefined &&
        field.y !== undefined &&
        field.width !== undefined &&
        field.height !== undefined
      if (!htmlDocument && !hasBox) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index],
          message: 'PDF fields require page, x, y, width, and height',
          params: { code: 'field.missing_box' },
        })
      }
    }

    const radioGroups = new Map<string, number>()
    for (const field of value.fields) {
      if (field.type !== 'radio' || field.group == null) {
        continue
      }
      const key = `${field.signerKey}:${field.group}`
      radioGroups.set(key, (radioGroups.get(key) ?? 0) + 1)
    }
    for (const [key, count] of radioGroups) {
      if (count < 2) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields'],
          message: `Radio group "${key}" needs at least two options`,
          params: { code: 'field.radio_group_too_small' },
        })
      }
    }

    const chosenRadios = new Map<string, number>()
    for (const [index, field] of value.fields.entries()) {
      if (field.type !== 'radio') continue
      const chosen = chosenRadioValue(field.value)
      if (chosen === undefined) continue
      if (chosen !== field.name) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'value'],
          message: `Radio "${field.name}" must equal its name when selected`,
          params: { code: 'field.radio_value' },
        })
        continue
      }
      if (!field.group) continue
      const key = `${field.signerKey}:${field.group}`
      chosenRadios.set(key, (chosenRadios.get(key) ?? 0) + 1)
    }
    for (const [key, count] of chosenRadios) {
      if (count > 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields'],
          message: `Radio group "${key}" has more than one option selected`,
          params: { code: 'field.radio_multiple' },
        })
      }
    }

    const extraAnswererEmails = new Set<string>()
    for (const [index, signer] of value.signers.entries()) {
      const questions = signer.questions
      if (!questions) {
        continue
      }
      const answerer = questions.answerer
      if ('signerKey' in answerer) {
        if (answerer.signerKey === signer.key) {
          ctx.addIssue({
            code: 'custom',
            path: ['signers', index, 'questions', 'answerer', 'signerKey'],
            message: 'A signer cannot answer their own questions',
            params: { code: 'answerer.is_signer_self' },
          })
        }
        if (!signerKeys.has(answerer.signerKey)) {
          ctx.addIssue({
            code: 'custom',
            path: ['signers', index, 'questions', 'answerer', 'signerKey'],
            message: `Unknown answerer signer key "${answerer.signerKey}"`,
            params: { code: 'answerer.unknown_signer' },
          })
        }
        continue
      }
      if (answerer.email === signer.email) {
        ctx.addIssue({
          code: 'custom',
          path: ['signers', index, 'questions', 'answerer', 'email'],
          message: 'A signer cannot answer their own questions',
          params: { code: 'answerer.is_signer_self' },
        })
      }
      extraAnswererEmails.add(answerer.email)
    }
    const signerEmails = new Set(value.signers.map((signer) => signer.email))
    for (const email of extraAnswererEmails) {
      if (signerEmails.has(email)) {
        ctx.addIssue({
          code: 'custom',
          path: ['signers'],
          message: `Answerer email "${email}" is already a signer; use answerer.signerKey`,
          params: { code: 'answerer.email_is_signer' },
        })
      }
    }
  })

/**
 * The JSON `payload` a caller may send. `retentionDays` may be left out;
 * parsing applies the default. {@link CreateEnvelopeInput} is that parsed value.
 */
export type CreateEnvelopePayload = z.input<typeof createEnvelopeInputSchema>

export type CreateEnvelopeInput = z.output<typeof createEnvelopeInputSchema>

export const voidEnvelopeInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(1000),
  })
  .strict()

export type VoidEnvelopeInput = z.infer<typeof voidEnvelopeInputSchema>

/**
 * Later deadlines for a draft or pending envelope: its own, signers' own, or
 * both. The same deadline again changes nothing, so a retry is safe; an
 * earlier one is refused.
 */
export const extendEnvelopeInputSchema = z
  .object({
    expiresAt: z.string().datetime().optional().describe('A later deadline for the envelope.'),
    signers: z
      .array(z.object({ id: uuidSchema, expiresAt: z.string().datetime() }).strict())
      .min(1)
      .max(ENVELOPE_LIMITS.maxSigners)
      .optional()
      .describe(
        'Later deadlines of their own for signers who haven\'t finished. A signer\'s can\'t be after the envelope\'s (extend both at once), or before that of a signer earlier in the order.',
      ),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.expiresAt === undefined && value.signers === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: [],
        message: 'Give expiresAt, signers, or both',
        params: { code: 'envelope.extend_empty' },
      })
    }
    if (value.signers && !uniqueStrings(value.signers.map((signer) => signer.id))) {
      ctx.addIssue({
        code: 'custom',
        path: ['signers'],
        message: 'Each signer can appear once',
        params: { code: 'signer.duplicate_id' },
      })
    }
  })

export type ExtendEnvelopeInput = z.infer<typeof extendEnvelopeInputSchema>

/** The remind body's shape; `remindEnvelopeInputSchema` also accepts no body at all. */
export const remindEnvelopeBodySchema = z
  .object({
    signerId: uuidSchema.optional(),
  })
  .strict()

export const remindEnvelopeInputSchema = z.preprocess((value) => value ?? {}, remindEnvelopeBodySchema)

export type RemindEnvelopeInput = z.infer<typeof remindEnvelopeInputSchema>

const listLimitSchema = z.coerce.number().int().min(1).max(ENVELOPE_LIMITS.maxListLimit).optional()
const listOffsetSchema = z.coerce.number().int().min(0).optional()

export const listEnvelopesQuerySchema = z
  .object({
    status: envelopeStatusSchema.optional(),
    email: emailSchema.optional(),
    limit: listLimitSchema,
    offset: listOffsetSchema,
  })
  .strict()

export type ListEnvelopesQuery = z.infer<typeof listEnvelopesQuerySchema>

const envelopeSearchFields = {
  status: envelopeStatusSchema.optional(),
  email: emailSchema.optional(),
  /** Case-insensitive substring of the title. */
  q: z.string().trim().min(1).max(200).optional(),
  /** Exact match on one metadata entry; give both or neither. */
  metadataKey: z.string().trim().min(1).max(ENVELOPE_LIMITS.maxMetadataChars).optional(),
  metadataValue: z.string().trim().max(ENVELOPE_LIMITS.maxMetadataChars).optional(),
  limit: listLimitSchema,
  offset: listOffsetSchema,
}

function bothOrNeither(query: { metadataKey?: string | undefined; metadataValue?: string | undefined }): boolean {
  return (query.metadataKey === undefined) === (query.metadataValue === undefined)
}

const BOTH_OR_NEITHER = { message: 'Give both metadataKey and metadataValue, or neither', path: ['metadataKey'] }

/**
 * The dashboard's envelope search: the API's filters plus title and metadata.
 * Kept separate from `listEnvelopesQuerySchema` so the public API is unchanged.
 */
export const envelopeSearchQuerySchema = z.object(envelopeSearchFields).strict().refine(bothOrNeither, BOTH_OR_NEITHER)

export type EnvelopeSearchQuery = z.infer<typeof envelopeSearchQuerySchema>

/** Staff search across tenants. `mode` defaults to live there, so test envelopes show only when asked for. */
export const opsEnvelopeSearchQuerySchema = z
  .object({
    ...envelopeSearchFields,
    tenantId: uuidSchema.optional(),
    mode: tenantModeSchema.optional(),
  })
  .strict()
  .refine(bothOrNeither, BOTH_OR_NEITHER)

export type OpsEnvelopeSearchQuery = z.infer<typeof opsEnvelopeSearchQuerySchema>

export function resolveFieldRequired(field: FieldInput): boolean {
  return field.required ?? defaultRequired(field.type)
}
